import { useEffect, useState } from "react";
import { Plus, Pencil, ToggleLeft, ToggleRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const EMPTY = { name: "", display_order: 100, active: true };

export default function AdminPartCategories() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/master-data/part-categories");
      setRows(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const openNew = () => { setForm({ ...EMPTY, display_order: (rows.length + 1) * 10 }); setEditing({}); };
  const openEdit = (r) => { setForm({ ...EMPTY, ...r }); setEditing(r); };
  const closeDialog = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Category name is required");
    setSaving(true);
    try {
      const body = { ...form, display_order: Number(form.display_order) };
      if (editing?.id) await api.patch(`/admin/master-data/part-categories/${editing.id}`, body);
      else await api.post("/admin/master-data/part-categories", body);
      toast.success(editing?.id ? "Category updated" : "Category created");
      closeDialog();
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (r) => {
    try {
      await api.patch(`/admin/master-data/part-categories/${r.id}`, { name: r.name, display_order: r.display_order || 100, active: !r.active });
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete category "${r.name}"? If referenced by parts, it will just be deactivated.`)) return;
    try {
      const res = await api.delete(`/admin/master-data/part-categories/${r.id}`);
      if (res.data?.soft_delete) toast.info(`Deactivated (${res.data.referenced_by} parts reference it)`);
      else toast.success("Category deleted");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-part-categories-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Master Data</div>
          <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557]">Part Categories</h1>
          <p className="text-[13px] text-[#667085] mt-0.5">The vocabulary used to organise spare-parts across every garage.</p>
        </div>
        <Button onClick={openNew} data-testid="add-part-category-btn" className="h-9 bg-[#e63946] hover:bg-[#c92e3b] text-white font-semibold text-[13px]">
          <Plus size={14} className="mr-1" /> Add Category
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-[#E4E7EC] overflow-x-auto">
        <table className="min-w-full text-[12px]">
          <thead className="bg-[#F7F9FC] text-left text-[#667085] uppercase text-[10px] tracking-widest">
            <tr>
              <th className="px-4 py-2 w-20 text-right">Order</th>
              <th className="px-4 py-2">Category</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1FAEE]">
            {loading && <tr><td colSpan={4} className="px-4 py-8 text-center text-[#667085]">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-[#667085]">No categories yet.</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.id} data-testid={`pc-row-${r.id}`} className="hover:bg-[#F7F9FC]">
                <td className="px-4 py-2 text-right font-mono text-[#667085]">{r.display_order ?? "—"}</td>
                <td className="px-4 py-2 font-semibold text-[#1d3557]">{r.name}</td>
                <td className="px-4 py-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider ${r.active ? "bg-[#12B76A]/10 text-[#027A48] border-[#12B76A]/25" : "bg-[#F7F9FC] text-[#667085] border-[#E4E7EC]"}`}>
                    {r.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => openEdit(r)} data-testid={`pc-edit-${r.id}`} className="text-[#457b9d] hover:text-[#1d3557] p-1"><Pencil size={13} /></button>
                  <button onClick={() => toggleActive(r)} data-testid={`pc-toggle-${r.id}`} className="text-[#457b9d] hover:text-[#1d3557] p-1 ml-1">
                    {r.active ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                  </button>
                  <button onClick={() => del(r)} data-testid={`pc-del-${r.id}`} className="text-[#e63946] hover:opacity-80 p-1 ml-1"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-md" data-testid="pc-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit Category" : "Add Category"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">Category Name *</label>
              <Input data-testid="pc-form-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g., Brakes" className="h-9 mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">Display Order</label>
              <Input data-testid="pc-form-order" type="number" min="0" value={form.display_order} onChange={(e) => setForm((f) => ({ ...f, display_order: e.target.value }))} className="h-9 mt-1 font-mono" />
            </div>
            <label className="flex items-center gap-2 text-[13px] text-[#1d3557]">
              <input type="checkbox" data-testid="pc-form-active" checked={!!form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />
              Active
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} data-testid="pc-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="pc-save" className="bg-[#e63946] hover:bg-[#c92e3b] text-white">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
